<!-- src/views/ForgotPassword.vue -->
<template>
  <section class="auth section container" style="padding-top:120px">
    <div class="auth-card glass">
      <h1 class="h1 auth-title">重設密碼</h1>
      <p class="lead auth-sub">輸入註冊 Email，我們會寄送重設連結。</p>

      <form class="auth-form" @submit.prevent="submit">
        <label class="label">電子郵箱</label>
        <input
          v-model.trim="email"
          type="email"
          class="input"
          placeholder="you@example.com"
          required
        />

        <p v-if="errorMsg" class="error-msg">{{ errorMsg }}</p>

        <button
          class="btn btn--primary auth-btn"
          :class="{ 'btn--disabled': cooldown > 0 }"
          type="submit"
          :disabled="loading || cooldown > 0"
        >
          <template v-if="loading">寄送中...</template>
          <template v-else-if="cooldown > 0">重新寄送 ({{ cooldown }}s)</template>
          <template v-else>寄送重設連結</template>
        </button>
      </form>

      <p v-if="sent" class="auth-success">
        ✅ 如果該信箱存在，我們已寄出重設連結，請查收你的信箱。
      </p>

      <p class="muted auth-foot">
        想起密碼了？
        <router-link to="/login" class="auth-link">回登入</router-link>
      </p>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { apiForgot } from '@/utils/auth'

const email = ref('')
const sent = ref(false)
const loading = ref(false)
const errorMsg = ref('')
const cooldown = ref(0)

const emailRegex = /^\S+@\S+\.\S+$/
const COOLDOWN_KEY = 'forgot_cooldown_ts' // 持久化冷卻時間戳
let timer = null

function startCooldown(sec = 60) {
  cooldown.value = sec
  localStorage.setItem(COOLDOWN_KEY, String(Date.now()))
  clearInterval(timer)
  timer = setInterval(() => {
    if (cooldown.value <= 1) {
      clearInterval(timer)
      timer = null
      cooldown.value = 0
      localStorage.removeItem(COOLDOWN_KEY)
    } else {
      cooldown.value--
    }
  }, 1000)
}

onMounted(() => {
  const ts = Number(localStorage.getItem(COOLDOWN_KEY))
  if (Number.isFinite(ts) && ts > 0) {
    const elapsed = Math.floor((Date.now() - ts) / 1000)
    const left = 60 - elapsed
    if (left > 0) startCooldown(left)
    else localStorage.removeItem(COOLDOWN_KEY)
  }
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})

async function submit() {
  errorMsg.value = ''
  sent.value = false

  if (!email.value) {
    errorMsg.value = '請輸入 Email'
    return
  }
  if (!emailRegex.test(email.value)) {
    errorMsg.value = 'Email 格式不正確'
    return
  }
  if (cooldown.value > 0) return

  loading.value = true
  try {
    await apiForgot(email.value.trim())
    sent.value = true
    startCooldown(60)
  } catch (err) {
    errorMsg.value = '伺服器忙碌，請稍後再試'
    startCooldown(10)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.auth {
  display: grid;
  place-items: center;
}

.auth-card {
  width: min(520px, 92vw);
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 18px;
  padding: 26px 22px 20px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.auth-title {
  margin: 0 0 8px;
}

.auth-sub {
  margin: 0 0 18px;
  opacity: 0.9;
}

.auth-form {
  display: grid;
  gap: 10px;
}

.auth-btn {
  width: 100%;
  margin-top: 8px;
  transition: background-color 0.25s, color 0.25s, opacity 0.25s;
}

/* 灰色冷卻狀態樣式 */
.btn--disabled {
  background: rgba(255, 255, 255, 0.2) !important;
  color: rgba(255, 255, 255, 0.5) !important;
  cursor: not-allowed !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  opacity: 0.6 !important;
}

.auth-foot {
  margin-top: 14px;
}

.auth-success {
  margin-top: 14px;
  color: #7cff9f;
  font-weight: 600;
}

.auth-link {
  text-decoration: underline;
}

.error-msg {
  color: #f87171;
  font-size: 14px;
  margin: 4px 0 0;
}
</style>
